import { useCallback, useRef, useEffect } from "react";
import {
  useAuthState,
  useSignInWithEmailAndPassword,
  useSignOut,
  useCreateUserWithEmailAndPassword,
} from "react-firebase-hooks/auth";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db, app } from "../../config/firebase";

// Enhanced debug logging function - works in production too for diagnostics
const debug = (message, data) => {
  // Always log in production for diagnostics
  const logData = {
    timestamp: new Date().toISOString(),
    message,
    ...(data && typeof data === "object" ? data : { data }),
    environment: import.meta.env.MODE,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
  };

  // Also log to window for easier debugging
  if (typeof window !== "undefined") {
    if (!window.__authDebugLogs) {
      window.__authDebugLogs = [];
    }
    window.__authDebugLogs.push(logData);
    // Keep only last 50 logs
    if (window.__authDebugLogs.length > 50) {
      window.__authDebugLogs.shift();
    }
  }
};

export const useAuth = () => {
  const [user, authLoading, error] = useAuthState(auth);
  const staffDocRef = user ? doc(db, "staff", user.uid) : null;
  const [staffProfile, staffLoading, staffError] = useDocumentData(staffDocRef);

  const [signInWithEmail, , signInLoading, signInError] = useSignInWithEmailAndPassword(auth);
  const [signOut, signOutLoading, signOutError] = useSignOut(auth);
  const [createUserWithEmailAndPassword, , createLoading, createError] = useCreateUserWithEmailAndPassword(auth);

  // Initialize diagnostic function (lazy load)
  const diagnosticFunctionRef = useRef(null);
  const diagnosticsRunRef = useRef(false);

  const getDiagnosticFunction = useCallback(() => {
    if (!diagnosticFunctionRef.current) {
      try {
        const functions = getFunctions(app, "europe-west2");
        diagnosticFunctionRef.current = httpsCallable(functions, "diagnoseStaffAuth");
      } catch (err) {
        debug("Failed to initialize diagnostic function", err);
        return null;
      }
    }
    return diagnosticFunctionRef.current;
  }, []);

  // Helper function to call diagnostic API
  const runDiagnostics = useCallback(async () => {
    const diagnoseAuth = getDiagnosticFunction();
    if (!diagnoseAuth) {
      debug("Diagnostic function not available");
      return null;
    }

    try {
      debug("Running authentication diagnostics...");
      const result = await diagnoseAuth();
      debug("Diagnostic results", result.data);

      // Store diagnostics in window for easy access
      if (typeof window !== "undefined") {
        window.__authDiagnostics = result.data;
      }

      return result.data;
    } catch (err) {
      debug("Error running diagnostics", {
        error: err.message,
        code: err.code,
        details: err.details,
      });
      return null;
    }
  }, [getDiagnosticFunction]);

  // Automatically run diagnostics when staff profile is missing
  useEffect(() => {
    if (user && !staffProfile && !staffLoading && !diagnosticsRunRef.current) {
      debug("User authenticated but staff profile missing - running diagnostics", {
        userId: user.uid,
        email: user.email,
        expectedPath: `/staff/${user.uid}`,
        error: staffError?.message || "Unknown error",
      });

      diagnosticsRunRef.current = true;
      runDiagnostics().then(() => {
        // Reset after 30 seconds to allow re-running if needed
        setTimeout(() => {
          diagnosticsRunRef.current = false;
        }, 30000);
      });
    }
  }, [user, staffProfile, staffLoading, staffError, runDiagnostics]);

  // Enhanced debug auth state - always log for diagnostics
  debug("Auth state", {
    hasUser: !!user,
    userId: user?.uid,
    userEmail: user?.email,
    userEmailVerified: user?.emailVerified,
    authLoading,
    staffLoading,
    hasStaffProfile: !!staffProfile,
    staffProfileData: staffProfile
      ? {
          email: staffProfile.email,
          displayName: staffProfile.displayName,
          role: staffProfile.role,
          status: staffProfile.status,
        }
      : null,
    staffError: staffError
      ? {
          message: staffError.message,
          code: staffError.code,
          name: staffError.name,
          stack: staffError.stack,
        }
      : null,
    staffDocRef: staffDocRef?.path,
    authError: error
      ? {
          message: error.message,
          code: error.code,
          name: error.name,
        }
      : null,
    signInError: signInError
      ? {
          message: signInError.message,
          code: signInError.code,
          name: signInError.name,
        }
      : null,
    signOutError: signOutError
      ? {
          message: signOutError.message,
          code: signOutError.code,
          name: signOutError.name,
        }
      : null,
    createError: createError
      ? {
          message: createError.message,
          code: createError.code,
          name: createError.name,
        }
      : null,
  });

  // Consider auth as loading if either Firebase auth or staff profile is loading
  // But only if we have a user and are waiting for their profile
  const isAuthLoading = authLoading || signInLoading || (user && staffLoading);

  const signIn = useCallback(
    async (email, password) => {
      debug("Sign in attempt started", {
        email,
        hasPassword: !!password,
        projectId: app.options?.projectId,
        apiKey: app.options?.apiKey?.substring(0, 20) + "...",
      });

      try {
        const result = await signInWithEmail(email, password);

        if (!result) {
          // Check signInError for more details
          const errorDetails = signInError
            ? {
                code: signInError.code,
                message: signInError.message,
                name: signInError.name,
                stack: signInError.stack,
              }
            : null;

          debug("Sign in failed - no result", {
            email,
            signInError: errorDetails,
            // Check if there's a network error or API error
            networkError: signInError?.code === "auth/network-request-failed",
            apiError: signInError?.code?.startsWith("auth/"),
          });

          return {
            success: false,
            error: signInError?.message || "Authentication failed. Please check your credentials and try again.",
            code: signInError?.code,
            diagnostics: {
              step: "signInWithEmail",
              result: null,
              signInError: errorDetails,
              firebaseConfig: {
                projectId: app.options?.projectId,
                authDomain: app.options?.authDomain,
                apiKeyPrefix: app.options?.apiKey?.substring(0, 20),
              },
            },
          };
        }

        debug("Sign in successful - Firebase Auth", {
          uid: result.user.uid,
          email: result.user.email,
          emailVerified: result.user.emailVerified,
        });

        // Wait a moment for Firestore to potentially load
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Check if staff document exists
        try {
          const staffDoc = await getDoc(doc(db, "staff", result.user.uid));

          debug("Staff document check", {
            exists: staffDoc.exists(),
            data: staffDoc.exists() ? staffDoc.data() : null,
            path: staffDoc.ref.path,
          });

          if (!staffDoc.exists()) {
            debug("Staff document missing after sign in", {
              uid: result.user.uid,
              expectedPath: `/staff/${result.user.uid}`,
            });

            // Run diagnostics to get detailed information
            const diagnosticsResult = await runDiagnostics();

            return {
              success: false,
              error: "Staff profile not found. Please contact support.",
              code: "staff-profile-missing",
              diagnostics: {
                uid: result.user.uid,
                email: result.user.email,
                expectedPath: `/staff/${result.user.uid}`,
                serverDiagnostics: diagnosticsResult,
              },
            };
          }
        } catch (firestoreErr) {
          debug("Error checking staff document", {
            error: firestoreErr.message,
            code: firestoreErr.code,
            name: firestoreErr.name,
            uid: result.user.uid,
          });

          // Run diagnostics to get detailed information about the error
          const diagnosticsResult = await runDiagnostics();

          return {
            success: false,
            error: `Failed to load staff profile: ${firestoreErr.message}`,
            code: firestoreErr.code || "firestore-error",
            diagnostics: {
              uid: result.user.uid,
              firestoreError: {
                message: firestoreErr.message,
                code: firestoreErr.code,
                name: firestoreErr.name,
              },
              serverDiagnostics: diagnosticsResult,
            },
          };
        }

        debug("Sign in fully successful", { uid: result.user.uid });
        return { success: true, user: result.user };
      } catch (err) {
        // Capture full error details including response data
        const fullError = {
          message: err.message,
          code: err.code,
          name: err.name,
          stack: err.stack,
          // Capture any additional error properties
          customData: err.customData,
          response: err.response,
          // Check for Firebase Auth specific error codes
          authErrorCode: err.code,
        };

        debug("Sign in error caught", {
          ...fullError,
          email,
          firebaseConfig: {
            projectId: app.options?.projectId,
            authDomain: app.options?.authDomain,
            apiKeyPrefix: app.options?.apiKey?.substring(0, 20),
          },
        });

        let errorMessage = "Authentication failed";
        let diagnostics = {
          error: fullError,
          firebaseConfig: {
            projectId: app.options?.projectId,
            authDomain: app.options?.authDomain,
            apiKeyPrefix: app.options?.apiKey?.substring(0, 20),
          },
        };

        if (err.code === "auth/operation-not-allowed") {
          errorMessage = "Email/Password authentication is not enabled.";
        } else if (err.code === "auth/user-disabled") {
          errorMessage = "This account has been disabled.";
        } else if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password") {
          errorMessage = "Invalid email or password.";
        } else if (err.code === "auth/network-request-failed") {
          errorMessage = "Network error. Please check your connection.";
          diagnostics.networkError = true;
        } else if (err.code === "auth/too-many-requests") {
          errorMessage = "Too many failed attempts. Please try again later.";
        }

        // For non-credential errors, run diagnostics to get more info
        if (err.code && !err.code.includes("wrong-password") && !err.code.includes("user-not-found")) {
          try {
            const diagnosticsResult = await runDiagnostics();
            diagnostics.serverDiagnostics = diagnosticsResult;
          } catch (diagErr) {
            debug("Failed to run diagnostics", diagErr);
          }
        }

        return {
          success: false,
          error: errorMessage,
          code: err.code,
          diagnostics,
        };
      }
    },
    [signInWithEmail, signInError, app]
  );

  const createUser = useCallback(
    async (email, password, displayName = "", phoneNumber = "", department = "", jobTitle = "") => {
      try {
        const result = await createUserWithEmailAndPassword(email, password);

        if (!result) {
          debug("Create user failed - no result");
          return {
            success: false,
            error: "Failed to create account. Please try again.",
          };
        }

        try {
          const now = serverTimestamp();

          // Create a more comprehensive staff data object
          const staffData = {
            // Basic profile information
            email: result.user.email,
            displayName: displayName || "",
            phoneNumber: phoneNumber || "",
            department: department || "",
            jobTitle: jobTitle || "",
            photoURL: "",

            // Status and activity tracking
            status: "online",
            isOnline: true,
            statusUpdatedAt: now,
            lastLogin: now,
            lastActive: now,

            // Password reset fields
            passwordResetRequested: false,
            passwordResetTimestamp: null,

            // Permissions
            role: "staff",

            // Audit tracking
            createdAt: now,
            updatedAt: now,
          };

          await setDoc(doc(db, "staff", result.user.uid), staffData);
        } catch (firestoreErr) {
          debug("Error creating staff document", firestoreErr);
          return {
            success: false,
            error: "Account created but failed to set up profile. Error: " + firestoreErr.message,
          };
        }

        return { success: true, user: result.user };
      } catch (err) {
        debug("Create user error", err);
        let errorMessage = "Registration failed";

        if (err.code === "auth/operation-not-allowed") {
          errorMessage = "Email/Password authentication is not enabled.";
        } else if (err.code === "auth/email-already-in-use") {
          errorMessage = "This email is already registered.";
        } else if (err.code === "auth/invalid-email") {
          errorMessage = "Invalid email address format.";
        } else if (err.code === "auth/weak-password") {
          errorMessage = "Password is too weak. Please use a stronger password.";
        }

        return {
          success: false,
          error: errorMessage,
          code: err.code,
        };
      }
    },
    [createUserWithEmailAndPassword]
  );

  const logout = useCallback(async () => {
    try {
      await signOut();
      return { success: true };
    } catch (err) {
      debug("Logout error", err);
      return {
        success: false,
        error: err.message || "Sign out failed",
      };
    }
  }, [signOut]);

  // Request password reset function
  const requestPasswordReset = useCallback(async () => {
    if (!user || !staffProfile) {
      debug("No user or staff profile found for password reset");
      return {
        success: false,
        error: "You must be logged in to request a password reset",
      };
    }

    try {
      // Update staff document to mark password reset as requested
      await setDoc(
        doc(db, "staff", user.uid),
        {
          passwordResetRequested: true,
          passwordResetTimestamp: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      return { success: true };
    } catch (err) {
      debug("Password reset request error", err);
      return {
        success: false,
        error: err.message || "Failed to request password reset",
      };
    }
  }, [user, staffProfile]);

  // Admin function to reset password
  const resetPassword = useCallback(async (email) => {
    try {
      const { sendPasswordResetEmail } = await import("firebase/auth");
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (err) {
      debug("Password reset error", err);
      let errorMessage = "Failed to send password reset email";

      if (err.code === "auth/user-not-found") {
        errorMessage = "No user found with this email address.";
      } else if (err.code === "auth/invalid-email") {
        errorMessage = "Invalid email address format.";
      }

      return {
        success: false,
        error: errorMessage,
        code: err.code,
      };
    }
  }, []);

  // Update user profile data
  const updateProfile = useCallback(
    async (profileData) => {
      if (!user) {
        return {
          success: false,
          error: "You must be logged in to update your profile",
        };
      }

      try {
        await setDoc(
          doc(db, "staff", user.uid),
          {
            ...profileData,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        return { success: true };
      } catch (err) {
        debug("Profile update error", err);
        return {
          success: false,
          error: err.message || "Failed to update profile",
        };
      }
    },
    [user]
  );

  // Update last active time
  const updateLastActive = useCallback(async () => {
    if (!user) return;

    try {
      await setDoc(
        doc(db, "staff", user.uid),
        {
          lastActive: serverTimestamp(),
          isOnline: true,
          status: "online",
        },
        { merge: true }
      );
    } catch (err) {
      debug("Last active update error", err);
    }
  }, [user]);

  // Enhanced error object with diagnostics
  const allErrors = [error, staffError, signInError, signOutError, createError].filter(Boolean);

  const combinedError =
    allErrors.length > 0
      ? {
          message: allErrors[0].message,
          code: allErrors[0].code,
          name: allErrors[0].name,
          allErrors: allErrors.map((e) => ({
            message: e.message,
            code: e.code,
            name: e.name,
          })),
        }
      : null;

  // Log authentication status periodically
  if (user && !staffProfile && !staffLoading) {
    debug("User authenticated but staff profile missing", {
      userId: user.uid,
      email: user.email,
      expectedPath: `/staff/${user.uid}`,
      error: staffError?.message || "Unknown error",
    });
  }

  return {
    user,
    staffProfile,
    isAuthenticated: !!user && !!staffProfile,
    isLoading: isAuthLoading || signInLoading || signOutLoading || createLoading,
    error: combinedError,
    signIn,
    logout,
    createUser,
    resetPassword,
    requestPasswordReset,
    updateProfile,
    updateLastActive,
    currentUser: user,
    // Diagnostic helpers
    diagnostics: {
      authState: {
        hasUser: !!user,
        userId: user?.uid,
        userEmail: user?.email,
        hasStaffProfile: !!staffProfile,
        isLoading: isAuthLoading || staffLoading,
      },
      errors: combinedError,
      // Access debug logs via window.__authDebugLogs
      getDebugLogs: () => (typeof window !== "undefined" ? window.__authDebugLogs : []),
      // Access server diagnostics via window.__authDiagnostics
      getServerDiagnostics: () => (typeof window !== "undefined" ? window.__authDiagnostics : null),
      // Manually run diagnostics
      runDiagnostics,
    },
  };
};

export default useAuth;
